import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_INTEGRATION_CREATE_PANEL } from '../../../webcomponents.constants';
import { GridViewWebComponent } from '../../../grid/gridView';
import { Column, ColumnKeys, FILTERS, IntegrationsGridViewData, PREDEFINED_FILTERS_KEYS } from '../types';
import { ViewConfiguration } from '../../../grid/gridView/types';
import IntegrationsGridDataProvider from '../dataProvider/IntegrationsGridDataProvider';
import { TemplateState } from '../../../types/common/types';
import { responseStatusRenderer, statusRenderer, typeRenderer } from './columnRenderers';
import { Integration, ConnectivityError } from '../../common/types';
import IntegrationDetailsPanelComponent from '../../integrationDetailsPanel';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { IntegrationCreatePanelWebComponent } from '../../addIntegrationPanel';
import { ElementClass } from '../../../utils/htmlElement.utils';
import { htmlEncode } from '../../../utils/escape.utils';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { SensorConnectivityErrors } from '../../sensors/constants';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';
import { dependentLicenses } from '../../../company/common/licensingTab/LicensingEnums';
import { LicenseStatus } from '../../../license/types/LicenseStatus';
import { IntegrationType } from '../../types/integration-type';

export default class IntegrationsGridView extends BaseWebComponentView {
  private gridDataProvider: IntegrationsGridDataProvider;
  public dialog: GzDialogWebComponent;
  public dom: {
    $wrapper: HTMLElement;
    $gridContainer: HTMLElement;
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $noResultsContainer: HTMLElement;
    $noResultsMessage: GzTitleWebComponent;
    $noResultsAdditionalText: GzTextWebComponent;
    $noResultsAddNewButton: GzButtonWebComponent;
    $noResultsAddNewTooltip: GzTooltipWebComponent;
    $addNewButton: GzButtonWebComponent;
    $addNewButtonTooltip: GzTooltipWebComponent;
    $addIntegration: IntegrationCreatePanelWebComponent;
  };

  getTranslations() {
    this.translations = this.stringsTranslator.getSensorsIntegrationsStrings();

    return this.translations;
  }

  setTranslatedStrings() {
    this.dom.$noResultsMessage.innerText = this.translations.noResultsMessage;
    this.dom.$noResultsAdditionalText.innerText = this.translations.noResultsAdditionalText;
    this.dom.$noResultsAddNewButton.innerText = this.translations.noResultsAddNewButtonLabel;
    this.dom.$addNewButton.innerText = this.dependencies.translateFn('Add New');
  }

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridContainer = this.shadow.querySelector('.grid');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<Column, ColumnKeys>;
    this.dom.$noResultsContainer = this.shadow.querySelector('#no-results');
    this.dom.$noResultsMessage = this.shadow.querySelector('#no-results-message');
    this.dom.$noResultsAdditionalText = this.shadow.querySelector('#no-results-additional-text');
    this.dom.$noResultsAddNewButton = this.shadow.querySelector('#no-results-add-new-button');
    this.dom.$noResultsAddNewTooltip = this.shadow.querySelector('#no-results-add-new-tooltip');
    this.dom.$addNewButton = this.shadow.querySelector('#addNewButton');
    this.dom.$addNewButtonTooltip = this.shadow.querySelector('#addNewButtonTooltip');
    this.dom.$addIntegration = this.shadow.querySelector(WEB_COMPONENT_INTEGRATION_CREATE_PANEL);
    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start();
    }

    this.getTranslations();
    this.setTranslatedStrings();
  }

  buildSuccessView(data: IntegrationsGridViewData) {
    this.dom.$addIntegration.injectDependencies(this.dependencies);
    this.dom.$addIntegration.licenses = data.data.licenses;
    this.dom.$addIntegration.start();
    this.resetTemplateState(this.dom.$wrapper);

    this.buildGridView(data);

    if (!this.hasAtLeastOneActiveBaseLicense(data.data.licenses)) {
      this.dom.$addNewButton.disabled = true;
      if (this.dom.$addNewButtonTooltip) {
        this.dom.$addNewButtonTooltip.tooltipText = this.translations.cannotAddIntegrationTooltip;
      }
      this.dom.$noResultsAddNewButton.disabled = true;
      this.dom.$noResultsAddNewTooltip.tooltipText = this.translations.cannotAddIntegrationTooltip;
    }
  }

  private buildGridView(data: IntegrationsGridViewData) {
    this.dom.$grid.propertiesSet = false;
    this.gridDataProvider = new IntegrationsGridDataProvider(this.dependencies);
    this.dom.$grid.dataProvider = this.gridDataProvider;

    const view: ViewConfiguration<Column, ColumnKeys> = data.views[0];
    view.filters = FILTERS;
    if (this.dependencies.isPartnerCompany()) {
      view.filters.push({ companyName: { contains: '' } });
    }
    this.gridDataProvider.setFilters({
      filters: view.filters,
      viewId: view.id,
    });

    this.dom.$grid.viewId = view.id;
    this.dom.$grid.filters = view.filters;
    this.dom.$grid.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$grid.columns = view.displayedColumns;
    this.dom.$grid.pageSize = view.pageSize;
    this.dom.$grid.pageNumber = view.pageNumber as number;
    this.dom.$grid.isCompactViewChecked = view.compactView;
    this.dom.$grid.allColumnDetails = data.allColumns;
    this.dom.$grid.defaultViewProps = data.defaultViewProps;
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.TYPE]: typeRenderer.bind(this),
      [ColumnKeys.STATUS]: statusRenderer.bind(this),
      [ColumnKeys.RESPONSE_STATUS]: responseStatusRenderer.bind(this),
    };
    this.dom.$grid.disableGridSettings = true;
    this.dom.$grid.hideHeaderActions = false;
    this.dom.$grid.hideFooter = false;
    this.dom.$grid.enableRowClickEvent = true;
    this.dom.$grid.showToolbarRefresh = false;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.showGlobalActions = true;
    this.dom.$grid.pageSizeOptions = [
      {
        title: '10',
        value: '10',
      },
      {
        title: '20',
        value: '20',
      },
      {
        title: '30',
        value: '30',
      },
      {
        title: '50',
        value: '50',
      },
      {
        title: '100',
        value: '100',
      },
    ];
    this.dom.$grid.propertiesSet = true;
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  async handleGridVisibility(): Promise<void> {
    if (this.gridDataProvider.getUnfilteredRowsTotal() > 0) {
      await this.showGrid();
    } else {
      this.hideGrid();
    }
  }

  async showGrid(): Promise<void> {
    this.removeElementClass(this.dom.$gridContainer, ElementClass.HIDDEN);
    this.setElementClass(this.dom.$noResultsContainer, ElementClass.HIDDEN);
  }

  async openDetailsPanel(integration: Integration): Promise<void> {
    const detailsPanel = new IntegrationDetailsPanelComponent();

    detailsPanel.integration = integration;
    detailsPanel.injectDependencies(this.dependencies);
    await detailsPanel.start();
    document.body?.appendChild(detailsPanel);
  }

  private hideGrid(): void {
    this.setElementClass(this.dom.$gridContainer, ElementClass.HIDDEN);
    this.removeElementClass(this.dom.$noResultsContainer, ElementClass.HIDDEN);
  }

  modifyDialogTitle(title: string): void {
    if (this.dialog) {
      this.dialog.headline = title;
    }
  }

  openAddWindow(): void {
    const windowTitle = htmlEncode(this.stringsTranslator.getSensorSetupText());

    this.dom.$addIntegration.initWebcomponent();
    this.dom.$addIntegration.classList.remove(ElementClass.HIDDEN);
    const windowConfigs: GzDialog = {
      headline: windowTitle,
      bodyContent: this.dom.$addIntegration,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
      onCloseHandler: () => {
        this.dom.$addIntegration.controller.showCancelWarningDialog();
      },
    };
    this.dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
  }

  openEditWindow(integration: Integration) {
    if (!this.isConnected) {
      return;
    }

    const integrationName = this.stringsTranslator.getTranslatedIntegrationNameByType(integration.type);
    const editSensorTitle = this.dependencies.translateFn('Edit sensor - {title}', { title: integrationName });

    this.dom.$addIntegration.controller.editIntegration(integration).catch(console.error);
    this.dom.$addIntegration.classList.remove(ElementClass.HIDDEN);

    const windowConfigs: GzDialog = {
      headline: editSensorTitle,
      bodyContent: this.dom.$addIntegration,
      isOpen: true,
      closableOutsideBox: false,
      isWindow: true,
      onCloseHandler: () => {
        this.dom.$addIntegration.controller.showCancelWarningDialog();
      },
    };
    this.dialog = GzDialogWebComponent.buildDialogElement(
      windowConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );
  }

  public resetAddIntegrationWindow() {
    this.dom.$addIntegration.classList.add(ElementClass.HIDDEN);
    this.dom.$wrapper.appendChild(this.dom.$addIntegration);
  }

  /**
   *
   * @param errors
   */
  public showResponseError(errors: Array<ConnectivityError>) {
    if (errors.length === 0) {
      const errorMessage = this.stringsTranslator.translateSensorConnectivityErrorCodes(
        SensorConnectivityErrors.UNKNOWN_ERROR
      );
      this.showErrorToast(errorMessage);
      return;
    }

    errors.forEach(sensorError => {
      const errorCode = sensorError['errorCode'] ? sensorError['errorCode'] : SensorConnectivityErrors.UNKNOWN_ERROR;
      const errorMessage =
        this.translations.sensorNames[sensorError['sensorType']] +
        ': ' +
        this.stringsTranslator.translateSensorConnectivityErrorCodes(errorCode);
      this.showErrorToast(errorMessage, sensorError['errorUri']);
    });
  }

  /**
   *
   * @param errorMessage
   * @param viewMoreURI
   */
  public showErrorToast(errorMessage: string, viewMoreURI: string | null = null) {
    const toastConfig: GzToast = {
      message: errorMessage,
      severity: ToastSeverity.ERROR,
    };

    if (viewMoreURI) {
      toastConfig.keepOpen = true;
      toastConfig.hasAction = true;
      toastConfig.actionButtonText = this.dependencies.translateFn('View more');
      toastConfig.showInlineButton = true;
      toastConfig.actionCallback = () => {
        return window.open(viewMoreURI, '_blank');
      };
    }

    GzToastNotificationWebComponent.buildToastWrapperElement(toastConfig);
  }

  public showSuccessToast(errorMessage: string, severity: ToastSeverity) {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: errorMessage,
      severity: severity,
    });
  }

  private hasAtLeastOneActiveBaseLicense(licenses) {
    const baseLicenses = licenses.filter(license => {
      return !dependentLicenses.includes(license.productId);
    });
    return baseLicenses.some(license => {
      return license.status === LicenseStatus.ACTIVE;
    });
  }
}
